import React, { useState, useEffect, useMemo, useRef } from 'react';
import { INDUSTRIES, INDUSTRY_OTHER } from '@/utils/industries';

/**
 * Searchable industry dropdown — same behaviour as Business Plan wizard Step 1.
 * Uses bp-industry-select styles from BusinessPlanWizardPage.css.
 */
export function IndustrySelector({
  sector,
  other,
  onSectorChange,
  onOtherChange,
  error,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (sector && sector !== INDUSTRY_OTHER) {
      setQuery(sector);
    } else if (!sector) {
      setQuery('');
    }
  }, [sector]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        if (sector && sector !== INDUSTRY_OTHER) {
          setQuery(sector);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sector]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return INDUSTRIES;
    return INDUSTRIES.filter((option) => option.toLowerCase().includes(q));
  }, [query]);

  const selectOption = (option) => {
    onSectorChange(option);
    setQuery(option === INDUSTRY_OTHER ? '' : option);
    setOpen(false);
    if (option !== INDUSTRY_OTHER) {
      onOtherChange('');
    }
  };

  return (
    <div className={`bp-industry-select ${className}`.trim()} ref={containerRef}>
      <input
        className={`bp-input bp-industry-select__input ${error ? 'bp-input--error' : ''}`}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        placeholder="Search or select industry..."
        value={query}
        data-testid="field-industry"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (sector) onSectorChange('');
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            if (sector && sector !== INDUSTRY_OTHER) setQuery(sector);
          }
        }}
      />
      {open && (
        <ul className="bp-industry-select__menu" role="listbox">
          {filtered.length > 0 ? (
            filtered.map((option) => (
              <li key={option}>
                <button
                  type="button"
                  className={`bp-industry-select__option${sector === option ? ' bp-industry-select__option--selected' : ''}`}
                  role="option"
                  aria-selected={sector === option}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  {option}
                </button>
              </li>
            ))
          ) : (
            <li className="bp-industry-select__empty">No matching industries</li>
          )}
        </ul>
      )}
      {sector === INDUSTRY_OTHER && (
        <input
          className={`bp-input bp-industry-select__other ${error ? 'bp-input--error' : ''}`}
          type="text"
          value={other}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder="e.g. Marine Logistics, Fashion Design..."
        />
      )}
    </div>
  );
}

export default IndustrySelector;
